import React, { useContext, useMemo, useState } from 'react'
import {
  CenteredSpinner,
  Col,
  Row,
  useToaster
} from 'react-angle-dashboard-components'
import { useHistory } from 'react-router-dom'
import {
  EmployeeFirstRow,
  EmployeeInput,
  EmployeeNumber,
  EmployeeWrapper,
  InputLabel
} from '../../components/Employee'
import { useMembers } from '../../hooks/use-members'
import useStep from '../../hooks/use-step'
import { employeeService, memberService } from '../../services'
import { GbsStore } from '../../store'
import { Actions } from '../../store/actions'
import { GbsStep } from '../../store/interfaces'
import {
  Amount,
  EmployeesWrapper,
  Highlight,
  ListHeader,
  ListWrapper,
  Subtitle,
  Title
} from './styles'
import { Paths } from '../../routes/paths'

const stepKey = 'companyCensus'

export const CompanyCensus = () => {
  const history = useHistory()
  const { show } = useToaster()
  const { dispatch } = useContext(GbsStore)

  const { members, handleMemberChange, isLoadingMembers } = useMembers(
    memberService,
    employeeService
  )

  const [isCreatingMembers, setIsCreatingMembers] = useState(false)

  const stepConfig: Omit<GbsStep, 'data'> = useMemo(() => {
    return {
      bottomBar: {
        hideEditActions: true,
        buttons: [
          {
            text: 'Next',
            variant: 'primary',
            disabled: false,
            callback: () => {
              const isValid = members.every((e) => e.email)

              if (!isValid) {
                show({
                  message: 'Email is required',
                  type: 'danger'
                })

                return
              }

              dispatch({
                type: Actions.SetStepData,
                payload: {
                  stepKey,
                  stepData: { members }
                }
              })
              setIsCreatingMembers(true)

              memberService
                .createMembers(members)
                .then((r) => {
                  history.push(Paths.enrollment)
                })
                .catch((e: any) => {
                  show({
                    message: e.message || 'Failed to create members',
                    type: 'danger',
                    icon: 'icon-cancel'
                  })
                })
                .finally(() => setIsCreatingMembers(false))
            }
          }
        ]
      },
      topBar: {
        completedPercentage: 80
      }
    }
  }, [history, members, dispatch, show])

  useStep(stepKey, stepConfig, dispatch)

  if (isCreatingMembers || isLoadingMembers) {
    return <CenteredSpinner />
  }

  if (members.length === 0) {
    return <p>Error fetching employees, please contact Angle Health Support</p>
  }

  return (
    <EmployeesWrapper>
      <Title>Company Census</Title>
      <Subtitle>
        Let's add the rest of your employee details so they can begin open
        enrollment when you've finished onboarding
      </Subtitle>
      <ListWrapper>
        <ListHeader>
          <h3>
            <Highlight>Employees: </Highlight>
            <Amount>{members.length}</Amount>
          </h3>
        </ListHeader>
        {members.map((member, i) => (
          <EmployeeWrapper key={member.employeeIndex}>
            <Row>
              <Col>
                <EmployeeFirstRow className="d-flex align-items-baseline">
                  <Col xs={2}>
                    <EmployeeNumber className="mr-3">
                      #{member.employeeIndex}
                    </EmployeeNumber>
                    <EmployeeNumber className="mr-1">
                      {member.firstName}
                    </EmployeeNumber>
                  </Col>
                  <Col xs={8} className="d-flex align-items-baseline">
                    <EmployeeInput
                      type="text"
                      placeholder="Last Name"
                      className="bg-white"
                      value={member.lastName}
                      onChange={(e: any) =>
                        handleMemberChange(i, 'lastName', e.target.value)
                      }
                    />
                    <EmployeeInput
                      type="email"
                      placeholder={
                        member.firstName
                          ? `${member.firstName}'s Email`
                          : 'Email'
                      }
                      className="bg-white"
                      value={member.email}
                      onChange={(e: any) =>
                        handleMemberChange(i, 'email', e.target.value)
                      }
                    />
                  </Col>
                  <Col xs={2} className="d-flex align-items-baseline">
                    <InputLabel>Age: </InputLabel>
                    <InputLabel
                      style={{
                        width: '54px',
                        color: 'gray'
                      }}
                    >
                      {member.age}
                    </InputLabel>
                  </Col>
                </EmployeeFirstRow>
              </Col>
            </Row>
          </EmployeeWrapper>
        ))}
      </ListWrapper>
    </EmployeesWrapper>
  )
}

export default CompanyCensus
